import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authService } from '@services/api'
import { useAuthStore } from '@store'
import worldMap from '@assets/images/world-map-dots.png'
import { TextField, PasswordField, SubmitButton } from '@components/auth/AuthFields'

function AdminLoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await authService.login(formData)

      // Vérifier que c'est bien un admin: via user_type OU via la présence de users_id
      const isAdmin = response.user_type === 'admin' || !!response.user?.users_id
      if (!isAdmin) {
        setError('Accès réservé aux administrateurs.')
        return
      }

      login(response.user, response.token, 'admin')
      // Maintenir la compatibilité avec les pages admin qui lisent ces clés directement
      localStorage.setItem('admin_token', response.token)
      localStorage.setItem('admin_user', JSON.stringify(response.user))
      navigate('/admin/dashboard')
    } catch (err) {
      const msg = err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="home-font relative min-h-screen overflow-hidden flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(180deg, #FFFCF3 0%, #F8FAFC 100%)' }}
    >
      {/* Décor : carte du monde + halos */}
      <img src={worldMap} alt="" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 w-[140%] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.07]" />
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#FFD700] opacity-20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 h-[460px] w-[460px] rounded-full bg-[#FFD700] opacity-10 blur-3xl" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <span className="px-5 py-3">
            <img src="/Logo_LIGDI.png" alt="KUDHI" className="h-10 w-auto" />
          </span>
        </div>

        {/* Carte */}
        <div className="rounded-md bg-white p-7 sm:p-9 ring-1 ring-slate-200/70 shadow-[0_25px_60px_-25px_rgba(0,42,78,0.3)]">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#FFD700] text-[#002A4E]">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#B8860B]">Back-office</p>
              <h1 className="text-2xl font-extrabold tracking-tight text-[#002A4E]">Espace Admin</h1>
            </div>
          </div>
          <p className="mt-3 text-slate-500">Connectez-vous pour accéder au tableau de bord.</p>

          {/* Message d'erreur */}
          {error && (
            <div className="mt-6 flex items-start gap-2.5 rounded-md border border-red-200 bg-red-50 p-3.5 text-sm text-red-600">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-7 space-y-5">
            <TextField
              label="Email ou téléphone"
              icon="user"
              type="text"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="username"
              placeholder="admin@kudhi.net"
            />

            <PasswordField
              label="Mot de passe"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              placeholder="Votre mot de passe"
            />

            <SubmitButton loading={isLoading} loadingLabel="Connexion…">
              Se connecter
            </SubmitButton>
          </form>

          <div className="mt-7 flex items-center gap-3 rounded-md bg-slate-50 p-3.5 text-sm text-slate-500">
            <svg className="h-5 w-5 flex-shrink-0 text-[#B8860B]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            Accès réservé aux administrateurs et au staff KUDHI.
          </div>
        </div>

        {/* Pied */}
        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 font-semibold text-[#002A4E] transition-colors hover:text-[#B8860B]">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Retour au site
          </Link>
          <p className="text-slate-400">© {new Date().getFullYear()} KUDHI</p>
        </div>
      </div>
    </div>
  )
}

export default AdminLoginPage
